import { useParams, Link } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { SiteContentContext } from '../context/SiteContentContext';
import { CardsContext } from '../context/CardsContext';
import QuestionModal from '../components/QuestionModal';
import { useSeo, toDescription } from '../hooks/useSeo';
import '../styles/profile_page.css';

const TYPE_LABELS = { adventures: 'Путешествие', study: 'Обучение', races: 'Регата' };
const MONTHS_SHORT = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];

function nearestDate(dates) {
  const today = new Date().toISOString().slice(0, 10);
  const future = (dates || []).map(d => typeof d === 'object' ? d.isoDate : d).filter(d => d >= today).sort();
  if (!future.length) return null;
  const [y, m, day] = future[0].split('-');
  return `${Number(day)} ${MONTHS_SHORT[Number(m)-1]} ${y}`;
}

function RelatedCard({ card, to }) {
  const nearest = nearestDate(card.availableDates);
  const dur = card.durationDays > 1 ? `${card.durationDays} дн.` : null;
  const meta = [nearest && `📅 ${nearest}`, dur && `⏱ ${dur}`].filter(Boolean).join('  ·  ');
  return (
    <Link to={to} className="profile-card-link">
      {card.image && <div className="profile-card-img" style={{ backgroundImage: `url(${card.image})` }} />}
      <div className="profile-card-info">
        {card._type && <span className={`profile-card-badge profile-card-badge--${card._type}`}>{TYPE_LABELS[card._type]}</span>}
        <strong>{card.title}</strong>
        {card.price && <span className="profile-card-price">{card.price}</span>}
        {meta && <span className="profile-card-meta">{meta}</span>}
      </div>
    </Link>
  );
}

const YachtPage = ({ isPreview = false }) => {
  const { yachtId } = useParams();
  const { content } = useContext(SiteContentContext);
  const { cards, productionCards } = useContext(CardsContext);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);
  const p = isPreview ? '/preview' : '';

  const source = isPreview ? cards : productionCards;
  const toSlug = (name) => name.toLowerCase().replace(/\s+/g, '-');
  const yacht = (content.yachts || []).find(y => toSlug(y.name) === yachtId || String(y.id) === yachtId);

  useSeo(yacht ? {
    title: `Яхта ${yacht.name}`,
    description: toDescription(yacht.description) || `Яхта ${yacht.name} — флот парусной школы The Sailing Doctor.`,
    image: yacht.photo,
  } : { title: 'Яхта не найдена' });

  const [lightbox, setLightbox] = useState(null); // index into allImages

  const allImages = yacht ? [yacht.photo, ...(yacht.gallery || [])].filter(Boolean) : [];

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLightbox(i => (i + 1) % allImages.length);
      if (e.key === 'ArrowLeft')  setLightbox(i => (i - 1 + allImages.length) % allImages.length);
      if (e.key === 'Escape')     setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, allImages.length]);

  if (!yacht) {
    return (
      <main className="profile-page">
        <div className="profile-not-found">
          <h2>Яхта не найдена</h2>
          <Link to={`${p}/fleet`} className="back-link">← Флот</Link>
        </div>
      </main>
    );
  }

  const allCards = [
    ...(source.adventures || []).map(c => ({ ...c, _type: 'adventures' })),
    ...(source.study || []).map(c => ({ ...c, _type: 'study' })),
    ...(source.races || []).map(c => ({ ...c, _type: 'races' })),
  ].filter(c => c.isPublished && (String(c.yachtId) === yachtId || c.yachtName === yacht.name));

  const gallery = (yacht.gallery || []).filter(Boolean);

  return (
    <main>
      <div className="profile-page">
      <div className="profile-hero">
        {yacht.photo && (
          <div
            className="profile-hero-image"
            style={{ backgroundImage: `url(${yacht.photo})`, cursor: 'zoom-in' }}
            onClick={() => setLightbox(0)}
          />
        )}
        <div className="profile-hero-content">
          <div className="profile-badge">⛵ Яхта</div>
          <h1>{yacht.name}</h1>
          {yacht.model && <p className="profile-subtitle">{yacht.model}</p>}
        </div>
      </div>

      <div className="profile-body">
        <div className="profile-main">
          {yacht.description && (
            <section className="profile-section">
              <h2>О яхте</h2>
              <p>{yacht.description}</p>
            </section>
          )}

          {gallery.length > 0 && (
            <section className="profile-section">
              <h2>Фотографии</h2>
              <div className="yacht-gallery-grid">
                {gallery.map((url, i) => (
                  <div
                    key={i}
                    className="yacht-gallery-item"
                    style={{ backgroundImage: `url(${url})` }}
                    onClick={() => setLightbox(i + 1)}
                  />
                ))}
              </div>
            </section>
          )}

          {allCards.length > 0 && (
            <section className="profile-section">
              <h2>Мероприятия на этой яхте</h2>
              <div className="profile-cards">
                {allCards.map(card => {
                  const tp = { adventures: 'adventures', study: 'study', races: 'races', main: 'news' };
                  const path = tp[card.type] || 'card';
                  return <RelatedCard key={card.id} card={card} to={`${p}/${path}/${card.slug || card.id}`} />;
                })}
              </div>
            </section>
          )}
        </div>

        <aside className="profile-sidebar">
          <div className="profile-specs">
            <h3>Характеристики</h3>
            <ul>
              {yacht.length   && <li><span>Длина</span><strong>{yacht.length}</strong></li>}
              {yacht.cabins   && <li><span>Каюты</span><strong>{yacht.cabins}</strong></li>}
              {yacht.capacity && <li><span>Вместимость</span><strong>{yacht.capacity} чел.</strong></li>}
              {yacht.year     && <li><span>Год постройки</span><strong>{yacht.year}</strong></li>}
            </ul>
          </div>
          <Link to={`${p}/fleet`} className="profile-back-link">← Флот</Link>
        </aside>
      </div>

      {lightbox !== null && (
        <div className="yacht-lightbox" onClick={() => setLightbox(null)}>
          <button className="yacht-lb-close" onClick={() => setLightbox(null)}>✕</button>
          {allImages.length > 1 && (
            <button className="yacht-lb-arrow yacht-lb-arrow--left"
              onClick={e => { e.stopPropagation(); setLightbox(i => (i - 1 + allImages.length) % allImages.length); }}>‹</button>
          )}
          <img src={allImages[lightbox]} alt="" onClick={e => e.stopPropagation()} />
          {allImages.length > 1 && (
            <button className="yacht-lb-arrow yacht-lb-arrow--right"
              onClick={e => { e.stopPropagation(); setLightbox(i => (i + 1) % allImages.length); }}>›</button>
          )}
          <div className="yacht-lb-counter">{lightbox + 1} / {allImages.length}</div>
        </div>
      )}
      </div>
      <section className="cta-section">
        <h2>Остались вопросы?</h2>
        <p>Напишите нам — расскажем о яхте и доступных датах. Ответим в течение дня.</p>
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
      </section>
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Яхта" />
    </main>
  );
};

export default YachtPage;
